import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Eye, DownloadSimple, ArrowSquareOut } from "@phosphor-icons/react";
import { API_BASE, downloadFile } from "@/lib/api";
import { toast } from "sonner";

// Preview PDF documents in an inline iframe using a blob URL (auth via token).
export function PreviewButton({ endpointPath, baseName, title, testId = "preview-btn", size = "sm" }) {
  const [open, setOpen] = useState(false);
  const [blobUrl, setBlobUrl] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    return () => { if (blobUrl) URL.revokeObjectURL(blobUrl); };
  }, [blobUrl]);

  async function handleOpen() {
    setOpen(true);
    if (blobUrl) return;
    setLoading(true);
    try {
      const token = localStorage.getItem("access_token");
      const res = await fetch(`${API_BASE}${endpointPath}/pdf`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error("preview failed");
      const blob = await res.blob();
      setBlobUrl(URL.createObjectURL(blob));
    } catch (e) {
      toast.error("Preview failed");
      setOpen(false);
    } finally {
      setLoading(false);
    }
  }

  function handleClose(v) {
    setOpen(v);
    if (!v && blobUrl) {
      URL.revokeObjectURL(blobUrl);
      setBlobUrl("");
    }
  }

  async function handleDownload() {
    try {
      await downloadFile(`${endpointPath}/pdf`, `${baseName}.pdf`);
      toast.success("Downloaded PDF");
    } catch (e) { toast.error("Download failed"); }
  }

  return (
    <>
      <Button variant="outline" size={size} onClick={handleOpen} className="border-border" data-testid={testId}>
        <Eye size={14} className="mr-2" /> Preview
      </Button>
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent className="max-w-5xl w-[92vw] h-[88vh] p-0 flex flex-col gap-0 overflow-hidden" data-testid={`${testId}-dialog`}>
          <DialogHeader className="p-4 border-b border-border flex-row items-center justify-between space-y-0">
            <DialogTitle className="font-heading font-black text-base">{title || "Document Preview"}</DialogTitle>
            <div className="flex items-center gap-2 mr-6">
              {blobUrl && (
                <a href={blobUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 border border-border rounded hover:bg-muted" data-testid={`${testId}-open-tab`}>
                  <ArrowSquareOut size={12} /> Open in new tab
                </a>
              )}
              <Button size="sm" variant="outline" onClick={handleDownload} className="border-border" data-testid={`${testId}-download`}>
                <DownloadSimple size={14} className="mr-2" /> Download PDF
              </Button>
            </div>
          </DialogHeader>
          <div className="flex-1 bg-muted/30">
            {loading ? (
              <div className="h-full flex items-center justify-center text-sm text-muted-foreground">Loading preview…</div>
            ) : blobUrl ? (
              <iframe
                src={blobUrl}
                title={title || "Preview"}
                className="w-full h-full border-0"
                data-testid={`${testId}-frame`}
              />
            ) : (
              <div className="h-full flex items-center justify-center text-sm text-muted-foreground">No preview available.</div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
